/* Sensory PDP buybox + gallery behavior. Scoped per .cn-pdp root. */
(() => {
  const money = (cents, currency) => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency: currency || 'USD',
      }).format((cents || 0) / 100);
    } catch (e) {
      return ((cents || 0) / 100).toFixed(2);
    }
  };

  document.querySelectorAll('.cn-pdp').forEach((root) => {
    const currency = root.dataset.currency || 'USD';
    let variants = [];
    try {
      variants = JSON.parse(root.dataset.variants || '[]');
    } catch (e) {
      variants = [];
    }
    let galleryGroups = [];
    try {
      galleryGroups = JSON.parse(root.querySelector('[data-gallery-groups-json]')?.textContent || '[]');
    } catch (e) {
      galleryGroups = [];
    }

    const form = root.querySelector('form[data-type="add-to-cart-form"]');
    const variantInput = root.querySelector('[data-variant-id]');
    const submit = root.querySelector('[data-submit]');
    const submitLabel = root.querySelector('[data-submit-label]');
    const priceEl = root.querySelector('[data-price]');
    const compareEl = root.querySelector('[data-compare]');
    const saveEl = root.querySelector('[data-save]');
    const colorNameEl = root.querySelector('[data-selected-color]');
    const modelFitEl = root.querySelector('[data-model-fit-note]');
    const modelFitMap = JSON.parse(root.querySelector('[data-model-fit-json]')?.textContent || '{}');
    const sizeNameEl = root.querySelector('[data-selected-size]');
    const stickyBar = root.querySelector('[data-sticky-bar]');
    const stickyPrice = root.querySelector('[data-sticky-price]');
    const stickyCompare = root.querySelector('[data-sticky-compare]');
    const stickyLabel = root.querySelector('[data-sticky-label]');
    const stickyCta = root.querySelector('[data-sticky-cta]');

    const optionButtons = Array.from(
      root.querySelectorAll('.swatch-item[data-option-position], .v-size[data-option-position], .cn-size-chip[data-option-position]')
    );

    const readSelection = () => {
      const selected = {};
      ['.swatch-item.active', '.v-size.active'].forEach((scope) => {
        root.querySelectorAll(scope).forEach((btn) => {
          selected[btn.dataset.optionPosition] = btn.dataset.optionValue;
        });
      });
      // Drawer chips mirror the buybox size selection.
      root.querySelectorAll('.cn-size-chip.active').forEach((btn) => {
        selected[btn.dataset.optionPosition] = btn.dataset.optionValue;
      });
      return selected;
    };

    const findVariant = (selected) => {
      return (
        variants.find((variant) =>
          (variant.options || []).every((value, index) => {
            const wanted = selected[String(index + 1)];
            return !wanted || value === wanted;
          })
        ) || null
      );
    };

    const applyGalleryColor = (color) => {
      if (!galleryGroups.length) return;
      const group = galleryGroups.find((item) => item.optionValue === color || item.label === color);
      const mediaIds = new Set((group?.mediaIds || []).map(String));
      const hasUsableGroup = mediaIds.size > 0;
      root.querySelectorAll('.equal-photo-card, .peek-slide').forEach((card) => {
        card.classList.toggle('is-gallery-hidden', hasUsableGroup && !mediaIds.has(String(card.dataset.mediaId)));
      });
      root.querySelectorAll('[data-dot-media-id]').forEach((dot) => {
        dot.classList.toggle('is-gallery-hidden', hasUsableGroup && !mediaIds.has(String(dot.dataset.dotMediaId)));
      });
      root.querySelector('[data-peek-track]')?.scrollTo({ left: 0, behavior: 'auto' });
      root.querySelectorAll('.gallery-dot').forEach((dot, index) => dot.classList.toggle('active', !dot.classList.contains('is-gallery-hidden') && index === 0));
    };

    const paintOptionButtons = (selected) => {
      optionButtons.forEach((btn) => {
        const isActive = selected[btn.dataset.optionPosition] === btn.dataset.optionValue;
        const candidate = { ...selected, [btn.dataset.optionPosition]: btn.dataset.optionValue };
        const available = Boolean(findVariant(candidate)?.available);
        btn.classList.toggle('active', isActive);
        btn.disabled = !available;
        if (btn.getAttribute('role') === 'radio') btn.setAttribute('aria-checked', isActive ? 'true' : 'false');
      });
    };

    const paintVariant = (variant) => {
      if (!variant) return;
      if (variantInput) variantInput.value = variant.id;
      const priceText = money(variant.price, currency);
      if (priceEl) priceEl.textContent = priceText;
      if (stickyPrice) stickyPrice.textContent = priceText;
      const hasCompare = variant.compare_at_price && variant.compare_at_price > variant.price;
      if (compareEl) {
        if (hasCompare) {
          compareEl.textContent = money(variant.compare_at_price, currency);
          compareEl.style.display = '';
        } else {
          compareEl.style.display = 'none';
        }
      }
      if (saveEl) {
        if (hasCompare) {
          saveEl.textContent = 'Save ' + money(variant.compare_at_price - variant.price, currency);
          saveEl.style.display = '';
        } else {
          saveEl.style.display = 'none';
        }
      }
      if (stickyCompare) {
        if (hasCompare) {
          stickyCompare.textContent = money(variant.compare_at_price, currency);
          stickyCompare.style.display = '';
        } else {
          stickyCompare.style.display = 'none';
        }
      }
      const label = variant.available ? 'Add to cart · ' + priceText : 'Sold out';
      if (submit) submit.disabled = !variant.available;
      if (submitLabel) submitLabel.textContent = label;
      if (stickyLabel) stickyLabel.textContent = variant.available ? 'Add to cart' : 'Sold out';
      if (stickyCta) stickyCta.disabled = !variant.available;
    };

    const syncFromSelection = () => {
      const selected = readSelection();
      const colorBtn = root.querySelector('.swatch-item.active');
      const sizeBtn = root.querySelector('.v-size.active, .cn-size-chip.active');
      if (colorNameEl && colorBtn) colorNameEl.textContent = colorBtn.dataset.optionValue;
      if (modelFitEl && colorBtn) {
        const fitInfo = modelFitMap[colorBtn.dataset.optionValue] || '';
        modelFitEl.textContent = fitInfo;
        modelFitEl.hidden = !fitInfo;
      }
      if (sizeNameEl && sizeBtn) sizeNameEl.textContent = sizeBtn.dataset.optionValue;
      if (colorBtn) applyGalleryColor(colorBtn.dataset.optionValue);
      paintOptionButtons(selected);
      paintVariant(findVariant(selected));
      // The bundle builder mirrors the Canvas prototype by refreshing each
      // editor when the primary PDP color/size selection changes.
      root.dispatchEvent(new CustomEvent('tshirt:selection-change', {
        detail: {
          color: colorBtn?.dataset.optionValue || '',
          size: sizeBtn?.dataset.optionValue || '',
        },
      }));
    };

    optionButtons.forEach((btn) => {
      btn.addEventListener('click', () => {
        // Deactivate siblings sharing the same option position, then activate.
        optionButtons.forEach((other) => {
          if (other.dataset.optionPosition === btn.dataset.optionPosition) other.classList.remove('active');
        });
        btn.classList.add('active');
        syncFromSelection();
      });
    });

    /* Tabs */
    const tabList = root.querySelector('[data-tabs]');
    const tabCard = root.querySelector('[data-tab-card]');
    if (tabList && tabCard) {
      const tabs = Array.from(tabList.querySelectorAll('[role="tab"]'));
      const panes = Array.from(tabCard.querySelectorAll('[role="tabpanel"]'));
      const activate = (index, focus) => {
        tabs.forEach((tab, i) => {
          const active = i === index;
          tab.classList.toggle('active', active);
          tab.setAttribute('aria-selected', active ? 'true' : 'false');
          tab.tabIndex = active ? 0 : -1;
          if (active && focus) tab.focus();
          if (active) tabCard.dataset.activeColor = tab.dataset.color || 'blue';
        });
        panes.forEach((pane, i) => pane.classList.toggle('active', i === index));
      };
      tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => activate(i, false));
        tab.addEventListener('keydown', (event) => {
          if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
            event.preventDefault();
            const next = event.key === 'ArrowRight' ? (i + 1) % tabs.length : (i - 1 + tabs.length) % tabs.length;
            activate(next, true);
          }
        });
      });
    }

    /* Size drawer */
    const overlay = root.querySelector('[data-size-overlay]');
    const drawer = root.querySelector('[data-size-drawer]');
    let lastFocus = null;
    const setDrawer = (open) => {
      if (!overlay || overlay.classList.contains('open') === open) return;
      overlay.classList.toggle('open', open);
      overlay.setAttribute('aria-hidden', open ? 'false' : 'true');
      overlay.inert = !open;
      document.documentElement.style.overflow = open ? 'hidden' : '';
      if (open) {
        lastFocus = document.activeElement;
        if (drawer) drawer.focus();
      } else if (lastFocus && lastFocus.focus) {
        lastFocus.focus();
      }
    };
    const primaryGuideBody = drawer?.querySelector('.cn-size-drawer-body');
    const guideTitle = drawer?.querySelector('.cn-size-title');
    const primaryGuideTitle = guideTitle?.textContent;
    // Delegate because bundle editors are rebuilt when the offer changes.
    // Keep the original body intact so its size-chip listeners remain attached.
    root.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-size-guide-trigger], [data-bundle-size-guide]');
      if (!trigger || !drawer) return;
      const productId = trigger.dataset.bundleSizeGuide;
      const template = productId && root.querySelector(`[data-bundle-size-template="${productId}"]`);
      if (productId && !template) return;
      drawer.querySelector('[data-bundle-guide-body]')?.remove();
      primaryGuideBody.hidden = Boolean(template);
      guideTitle.textContent = template ? `${template.dataset.productTitle} · Size & fit` : primaryGuideTitle;
      if (template) drawer.append(template.content.cloneNode(true));
      setDrawer(true);
      drawer.scrollTop = 0;
    });
    // Only the visible guide participates in the modal's focus loop.
    drawer?.addEventListener('keydown', (event) => {
      if (event.key !== 'Tab') return;
      const controls = [...drawer.querySelectorAll('button, a[href], select, summary, [tabindex="0"]')]
        .filter((node) => !node.disabled && node.getClientRects().length);
      const first = controls[0], last = controls[controls.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === drawer)) {
        event.preventDefault(); last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault(); first?.focus();
      }
    });
    const closeBtn = root.querySelector('[data-size-close]');
    if (closeBtn) closeBtn.addEventListener('click', () => setDrawer(false));
    if (overlay) {
      overlay.addEventListener('click', (event) => {
        if (event.target === overlay) setDrawer(false);
      });
    }
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        setDrawer(false);
        closeLightbox();
      }
    });

    /* Lightbox (product.media only) */
    const lightbox = root.querySelector('[data-lightbox-overlay]');
    const lightboxImg = root.querySelector('[data-lightbox-image]');
    const tabSources = Array.from(root.querySelectorAll('[data-lightbox]')).map((btn) => btn.dataset.full || (btn.querySelector('img') && btn.querySelector('img').src)).filter(Boolean);
    let lightboxSources = tabSources.slice();
    /* Visible gallery cards form their own pool so prev/next never leaves the active color. */
    const gallerySourceList = () => {
      const seen = new Set();
      const list = [];
      root.querySelectorAll('.equal-photo-card[data-full], .peek-slide[data-full]').forEach((card) => {
        if (card.classList.contains('is-gallery-hidden')) return;
        const src = card.dataset.full;
        if (src && !seen.has(src)) { seen.add(src); list.push(src); }
      });
      return list;
    };
    let lightboxIndex = 0;
    /* Zoom + gestures mirror grounding layout B, scoped to this root. */
    const lightboxFrame = root.querySelector('.img-lightbox-frame');
    const lightboxZoom = root.querySelector('[data-lightbox-zoom]');
    const lightboxCount = root.querySelector('[data-lightbox-count]');
    const ZOOM_IN_SVG = '<svg width="20" height="20" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256" fill="currentColor"><path d="M152,112a8,8,0,0,1-8,8H120v24a8,8,0,0,1-16,0V120H80a8,8,0,0,1,0-16h24V80a8,8,0,0,1,16,0v24h24A8,8,0,0,1,152,112Zm77.66,117.66a8,8,0,0,1-11.32,0l-50.06-50.07a88.11,88.11,0,1,1,11.31-11.31l50.07,50.06A8,8,0,0,1,229.66,229.66ZM112,184a72,72,0,1,0-72-72A72.08,72.08,0,0,0,112,184Z"/></svg>';
    const ZOOM_OUT_SVG = '<svg width="20" height="20" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256" fill="currentColor"><path d="M152,112a8,8,0,0,1-8,8H80a8,8,0,0,1,0-16h64A8,8,0,0,1,152,112Zm77.66,117.66a8,8,0,0,1-11.32,0l-50.06-50.07a88.11,88.11,0,1,1,11.31-11.31l50.07,50.06A8,8,0,0,1,229.66,229.66ZM112,184a72,72,0,1,0-72-72A72.08,72.08,0,0,0,112,184Z"/></svg>';
    let zbScale = 1, zbPanX = 0, zbPanY = 0, zbLastTap = 0, zbGesture = null;
    const zbPointers = new Map();
    const paintZoom = () => {
      if (!lightboxImg || !lightboxFrame) return;
      const box = lightboxFrame.getBoundingClientRect();
      const maxX = box.width * (zbScale - 1) / 2, maxY = box.height * (zbScale - 1) / 2;
      zbPanX = Math.max(-maxX, Math.min(maxX, zbPanX));
      zbPanY = Math.max(-maxY, Math.min(maxY, zbPanY));
      lightboxImg.style.transform = 'translate(' + zbPanX + 'px,' + zbPanY + 'px) scale(' + zbScale + ')';
      lightboxImg.classList.toggle('is-zoomed', zbScale > 1);
      if (lightboxZoom) {
        lightboxZoom.innerHTML = zbScale > 1 ? ZOOM_OUT_SVG : ZOOM_IN_SVG;
        lightboxZoom.setAttribute('aria-label', zbScale > 1 ? 'Reset zoom' : 'Zoom in');
      }
    };
    const resetZoom = () => { zbScale = 1; zbPanX = zbPanY = 0; paintZoom(); };
    const toggleZoom = () => { zbScale = zbScale > 1 ? 1 : 2.5; zbPanX = zbPanY = 0; paintZoom(); };
    const paintCount = () => { if (lightboxCount) lightboxCount.textContent = (lightboxIndex + 1) + ' / ' + lightboxSources.length; };
    const endPointer = (event) => {
      if (!zbGesture) return;
      const gesture = zbGesture;
      zbPointers.delete(event.pointerId);
      if (zbPointers.size) return;
      zbGesture = null;
      if (gesture.pinch) return;
      const dx = event.clientX - gesture.x, dy = event.clientY - gesture.y;
      if (zbScale === 1 && Math.abs(dx) > 48 && Math.abs(dx) > Math.abs(dy) * 1.3) { showLightbox(lightboxIndex + (dx < 0 ? 1 : -1)); zbLastTap = 0; }
      else if (!gesture.moved && Date.now() - gesture.time < 350) {
        const now = Date.now();
        if (now - zbLastTap < 300) { toggleZoom(); zbLastTap = 0; } else zbLastTap = now;
      }
    };
    const showLightbox = (index) => {
      if (!lightbox || !lightboxImg || !lightboxSources.length) return;
      lightboxIndex = (index + lightboxSources.length) % lightboxSources.length;
      lightboxImg.src = lightboxSources[lightboxIndex];
      resetZoom();
      paintCount();
      lightboxImg.onload = () => {
        if (lightboxImg.naturalWidth) lightboxImg.width = lightboxImg.naturalWidth;
        if (lightboxImg.naturalHeight) lightboxImg.height = lightboxImg.naturalHeight;
      };
      lightbox.classList.add('active');
      document.documentElement.style.overflow = 'hidden';
      lightbox.focus();
    };
    const openLightbox = (src, pool) => {
      if (pool && pool.length) lightboxSources = pool;
      const index = lightboxSources.indexOf(src);
      showLightbox(index >= 0 ? index : 0);
    };
    const closeLightbox = () => {
      if (!lightbox || !lightbox.classList.contains('active')) return;
      lightbox.classList.remove('active');
      document.documentElement.style.overflow = '';
      zbPointers.clear();
      zbGesture = null;
      if (lightboxImg) { lightboxImg.removeAttribute('src'); lightboxImg.style.transform = ''; lightboxImg.classList.remove('is-zoomed'); }
    };
    root.querySelectorAll('[data-lightbox]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const full = btn.dataset.full || (btn.querySelector('img') && btn.querySelector('img').src);
        openLightbox(full, tabSources);
      });
    });
    root.querySelectorAll('.equal-photo-card[data-full], .peek-slide[data-full]').forEach((card) => {
      card.addEventListener('click', () => {
        const pool = gallerySourceList();
        if (pool.length) openLightbox(card.dataset.full, pool);
      });
    });
    if (lightbox) {
      lightbox.addEventListener('click', (event) => {
        if (event.target === lightbox) closeLightbox();
      });
      const lightboxClose = root.querySelector('[data-lightbox-close]');
      if (lightboxClose) lightboxClose.addEventListener('click', closeLightbox);
      const lightboxPrev = root.querySelector('[data-lightbox-prev]');
      const lightboxNext = root.querySelector('[data-lightbox-next]');
      if (lightboxPrev) lightboxPrev.addEventListener('click', (event) => { event.stopPropagation(); showLightbox(lightboxIndex - 1); });
      if (lightboxNext) lightboxNext.addEventListener('click', (event) => { event.stopPropagation(); showLightbox(lightboxIndex + 1); });
      lightbox.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowRight') showLightbox(lightboxIndex + 1);
        if (event.key === 'ArrowLeft') showLightbox(lightboxIndex - 1);
        if (event.key === '+' || event.key === '=') { event.preventDefault(); zbScale = Math.min(4, zbScale + 0.5); paintZoom(); }
        if (event.key === '-') { event.preventDefault(); zbScale = Math.max(1, zbScale - 0.5); paintZoom(); }
      });
      if (lightboxZoom) lightboxZoom.addEventListener('click', (event) => { event.stopPropagation(); toggleZoom(); });
      if (lightboxFrame) {
        lightboxFrame.addEventListener('pointerdown', (event) => {
          zbPointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
          try { lightboxFrame.setPointerCapture(event.pointerId); } catch (captureError) { /* noop */ }
          if (zbPointers.size === 1) zbGesture = { x: event.clientX, y: event.clientY, panX: zbPanX, panY: zbPanY, time: Date.now(), moved: false, pinch: false };
          if (zbPointers.size === 2) {
            const points = [...zbPointers.values()];
            zbGesture = { distance: Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y), scale: zbScale, pinch: true, moved: true };
          }
        });
        lightboxFrame.addEventListener('pointermove', (event) => {
          if (!zbPointers.has(event.pointerId) || !zbGesture) return;
          zbPointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
          if (zbPointers.size === 2) {
            const points = [...zbPointers.values()];
            zbScale = Math.max(1, Math.min(4, zbGesture.scale * Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y) / zbGesture.distance));
            paintZoom();
          } else if (!zbGesture.pinch) {
            const dx = event.clientX - zbGesture.x, dy = event.clientY - zbGesture.y;
            if (Math.hypot(dx, dy) > 8) zbGesture.moved = true;
            if (zbScale > 1) { zbPanX = zbGesture.panX + dx; zbPanY = zbGesture.panY + dy; paintZoom(); }
          }
        });
        lightboxFrame.addEventListener('pointerup', endPointer);
        lightboxFrame.addEventListener('pointercancel', () => { zbPointers.clear(); zbGesture = null; });
        lightboxFrame.addEventListener('wheel', (event) => { event.preventDefault(); zbScale = Math.max(1, Math.min(4, zbScale - event.deltaY * 0.005)); paintZoom(); }, { passive: false });
      }
    }

    /* Mobile peek dots stay aligned with the color-filtered media set. */
    const peekTrack = root.querySelector('[data-peek-track]');
    const dotsWrap = root.querySelector('[data-gallery-dots]');
    if (peekTrack && dotsWrap) {
      const syncDots = () => {
        const slides = Array.from(peekTrack.children).filter((slide) => !slide.classList.contains('is-gallery-hidden'));
        const dots = Array.from(dotsWrap.querySelectorAll('.gallery-dot')).filter((dot) => !dot.classList.contains('is-gallery-hidden'));
        const step = slides[1] ? slides[1].offsetLeft - slides[0].offsetLeft : Math.max(1, peekTrack.clientWidth);
        const index = Math.max(0, Math.min(slides.length - 1, Math.round(peekTrack.scrollLeft / step)));
        dots.forEach((dot, dotIndex) => dot.classList.toggle('active', dotIndex === index));
      };
      let ticking = false;
      peekTrack.addEventListener('scroll', () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => { syncDots(); ticking = false; });
      }, { passive: true });
      window.addEventListener('resize', syncDots);
      syncDots();
    }

    /* Tactile carousel (mobile) */
    const tactileTrack = root.querySelector('[data-tactile-track]');
    const tactilePrev = root.querySelector('[data-tactile-prev]');
    const tactileNext = root.querySelector('[data-tactile-next]');
    if (tactileTrack && tactilePrev && tactileNext) {
      const cards = Array.from(tactileTrack.querySelectorAll('article'));
      let tactileIndex = 0;
      const onlyMobile = window.matchMedia('(max-width: 900px)');
      const syncTactile = () => {
        tactileIndex = Math.max(0, Math.min(cards.length - 1, Math.round(tactileTrack.scrollLeft / Math.max(1, tactileTrack.clientWidth))));
        tactilePrev.disabled = tactileIndex === 0;
        tactileNext.disabled = tactileIndex === cards.length - 1;
      };
      const goTactile = (index) => {
        if (!onlyMobile.matches) return;
        tactileIndex = Math.max(0, Math.min(cards.length - 1, index));
        tactileTrack.scrollTo({ left: tactileIndex * tactileTrack.clientWidth, behavior: 'smooth' });
      };
      tactilePrev.addEventListener('click', () => goTactile(tactileIndex - 1));
      tactileNext.addEventListener('click', () => goTactile(tactileIndex + 1));
      tactileTrack.addEventListener('scroll', () => requestAnimationFrame(syncTactile), { passive: true });
      window.addEventListener('resize', syncTactile);
      syncTactile();
    }

    /* Sticky bar: reveal once the main CTA scrolls out of view */
    if (stickyBar && submit) {
      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => stickyBar.classList.toggle('is-hidden', entry.isIntersecting));
          },
          { rootMargin: '0px' }
        );
        observer.observe(submit);
      } else {
        stickyBar.classList.remove('is-hidden');
      }
    }
    if (stickyCta && form) {
      stickyCta.addEventListener('click', () => {
        if (form.requestSubmit) form.requestSubmit(submit);
        else if (submit) submit.click();
      });
    }

    /* Add to cart via existing cart infrastructure */
    if (form) {
      form.addEventListener('submit', async (event) => {
        // The shared offer builder submits grouped lines from this form.
        if (form.dataset.bundleManaged === 'true') return;
        event.preventDefault();
        if (!variantInput || !variantInput.value || (submit && submit.disabled)) return;
        if (submit) submit.disabled = true;
        const original = submitLabel ? submitLabel.textContent : '';
        if (submitLabel) submitLabel.textContent = 'Adding…';
        try {
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { Accept: 'application/json' },
            body: new FormData(form),
          });
          if (!response.ok) throw new Error('Cart add failed');
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
          if (submitLabel) submitLabel.textContent = 'Added';
          if (stickyLabel) stickyLabel.textContent = 'Added';
          window.setTimeout(() => syncFromSelection(), 1200);
        } catch (error) {
          if (submit) submit.disabled = false;
          if (submitLabel) submitLabel.textContent = original || 'Try again';
        }
      });
    }

    syncFromSelection();
  });
})();
